import { Component, version } from 'react';
import Raven from 'raven-js';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import I18n from 'I18n';
import UILink from 'UIComponents/link/UILink';
import PropTypes from 'prop-types';
import once from 'transmute/once';
import { jsx as _jsx } from "react/jsx-runtime";
let addRefreshAlert;
function _addRefreshAlert(id) {
  FloatingAlertStore.addAlert({
    id,
    type: 'danger',
    use: 'inline',
    titleText: I18n.text('componentErrorAlert.title'),
    message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "componentErrorAlert.message_jsx",
      options: {
        href: window.location.href
      },
      elements: {
        Link: UILink
      }
    }),
    // if they close the alert, reset the timer in case something else breaks
    onClose: () => {
      addRefreshAlert = once(_addRefreshAlert);
    }
  });
}

// if a bunch of things break at the same time, during a critsit or something,
// we don't want to show the alert a million times. show it once, and reset if
// they clear it (see onClose above)
addRefreshAlert = once(_addRefreshAlert);
/**
 * Usage:
 *   <ErrorBoundary boundaryName="MyComponent_MyError" ErrorComponent={MyError} errorProps={{ foo: 'bar' }}>
 *     <MyComponent />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends Component {
  static getDerivedStateFromError() {
    return {
      hasError: true
    };
  }
  constructor(props) {
    super(props);
    this.attemptToRemount = () => {
      const {
        boundaryName,
        showRefreshAlert
      } = this.props;
      if (showRefreshAlert && boundaryName) {
        addRefreshAlert = once(_addRefreshAlert);
        FloatingAlertStore.removeAlert(boundaryName);
      }
      this.setState({
        hasError: false
      });
    };
    this.state = {
      hasError: false
    };
  }
  componentDidCatch(error, info = {}) {
    const {
      shouldAutoReset,
      onComponentDidCatch,
      boundaryName,
      shouldReportAllErrors
    } = this.props;
    const isAutoResetting = shouldAutoReset(error);
    if (isAutoResetting) {
      this.setState({
        hasError: false
      });
    } else {
      if (this.props.showRefreshAlert) {
        addRefreshAlert(boundaryName);
      }
    }
    if (shouldReportAllErrors || !isAutoResetting) {
      this.reportErrors(error, info);
    }
    onComponentDidCatch(error);
  }

  // Passed to the ErrorComponent to allow for a manual reset of the component.

  reportErrors(error, info = {}) {
    const boundaryName = this.props.boundaryName || '';

    // @ts-expect-error newrelic ambient type requires a runtimedep on `react-rhumb` or redefining it (https://git.hubteam.com/HubSpot/react-rhumb/blob/master/react-rhumb/static/ambient-types/global.d.ts#L12
    if (window.newrelic && typeof window.newrelic.noticeError === 'function') {
      // @ts-expect-error newrelic ambient type requires a runtimedep on `react-rhumb` or redefining it (https://git.hubteam.com/HubSpot/react-rhumb/blob/master/react-rhumb/static/ambient-types/global.d.ts#L12
      window.newrelic.noticeError(error, {
        errorBoundaryName: boundaryName
      });
    }
    Raven.captureException(error, {
      extra: Object.assign({}, info, {
        version
      }),
      tags: {
        errorBoundaryName: boundaryName
      }
    });
  }
  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    const {
      ErrorComponent
    } = this.props;
    if (this.state.hasError && ErrorComponent) {
      return /*#__PURE__*/_jsx(ErrorComponent, Object.assign({}, this.props.errorProps, {
        attemptToRemount: this.attemptToRemount
      }));
    }
    return null;
  }
}
ErrorBoundary.propTypes = {
  // boundaryName: "<Component>_<Error Component>" (e.g. "FloatingForm_SidebarPropertyError")
  boundaryName: PropTypes.string,
  ErrorComponent: PropTypes.func,
  errorProps: PropTypes.object,
  children: PropTypes.node,
  onComponentDidCatch: PropTypes.func,
  /**
   * shouldAutoReset: (optional) attempts to refresh the component and returns a boolean value
   * When false is returned we show and log the error.
   */
  shouldAutoReset: PropTypes.func,
  showRefreshAlert: PropTypes.bool,
  shouldReportAllErrors: PropTypes.bool
};
ErrorBoundary.defaultProps = {
  showRefreshAlert: true,
  shouldAutoReset: __error => false,
  onComponentDidCatch: __error => {},
  shouldReportAllErrors: false
};
export default ErrorBoundary;